/**
 * Incident history (#2575), newest first. Duration is wall clock from
 * start to recover (or now if still open). "Send history" jumps into
 * the history tab filtered to this incident.
 */
import type { CodexCapacityAlertIncident } from '@nadoutong/contracts';
import { formatLocalTimestamp } from '../formatters.ts';
import { useT } from '../../i18n/index.ts';
import { Button, Table, TableEmpty, TBody, Td, Th, THead, Tr } from '../../ui/index.ts';
import { formatIncidentDurationMinutes } from './codexCapacityStatus.ts';

interface Props {
  readonly incidents: readonly CodexCapacityAlertIncident[];
  readonly now?: Date;
  readonly onViewHistory: (incidentId: string) => void;
}

export function CodexCapacityIncidentsTable({
  incidents,
  now = new Date(),
  onViewHistory,
}: Props): JSX.Element {
  const t = useT();
  return (
    <Table testId="codex-capacity-incidents-table">
      <THead>
        <Tr interactive={false}>
          <Th>{t('codex_capacity.incidents.column.started')}</Th>
          <Th>{t('codex_capacity.incidents.column.ended')}</Th>
          <Th>{t('codex_capacity.incidents.column.duration')}</Th>
          <Th num>{t('codex_capacity.incidents.column.fire_ratio')}</Th>
          <Th num>{t('codex_capacity.incidents.column.peak')}</Th>
          <Th num>{t('codex_capacity.incidents.column.elevated')}</Th>
          <Th num>{t('codex_capacity.incidents.column.recovered')}</Th>
          <Th>{t('codex_capacity.history.column.incident')}</Th>
        </Tr>
      </THead>
      <TBody>
        {incidents.length === 0 ? (
          <TableEmpty colSpan={8}>{t('codex_capacity.incidents.empty')}</TableEmpty>
        ) : (
          incidents.map((row) => {
            const minutes = formatIncidentDurationMinutes(row.started_at, row.recovered_at, now);
            return (
              <Tr
                key={row.id}
                interactive={false}
                data-testid={`codex-capacity-incident-row-${row.id}`}
              >
                <Td mono>{formatLocalTimestamp(row.started_at, { seconds: true })}</Td>
                <Td mono>
                  {row.recovered_at === null
                    ? t('codex_capacity.incidents.open')
                    : formatLocalTimestamp(row.recovered_at, { seconds: true })}
                </Td>
                <Td>
                  {minutes === null
                    ? '—'
                    : t('codex_capacity.incidents.duration_minutes', { minutes: String(minutes) })}
                </Td>
                <Td num>{formatPct(row.fire_window_pct)}</Td>
                <Td num>{formatPct(row.peak_window_pct)}</Td>
                <Td num>{String(row.fire_recipients)}</Td>
                <Td num>{String(row.recovery_recipients)}</Td>
                <Td>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onViewHistory(row.id)}
                    data-testid={`codex-capacity-incident-history-${row.id}`}
                  >
                    {t('codex_capacity.incidents.view_history')}
                  </Button>
                </Td>
              </Tr>
            );
          })
        )}
      </TBody>
    </Table>
  );
}

function formatPct(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return `${value.toFixed(1)}%`;
}
